import { ErrorHandler, inject, Injectable } from "@angular/core";
import { NavigationEnd, NavigationError, NavigationStart, Router } from "@angular/router";
import { filter, take } from "rxjs";

/**
 * Catches stale-chunk runtime errors caused by an old `index.html` (often
 * served from the service-worker cache) referencing JS bundles whose hashes
 * no longer exist on the server after a fresh deploy.
 *
 * HOW THE BUG MANIFESTED:
 *   1. Angular router updates the browser URL to the new route (e.g. /ai-agent/agent-transfer)
 *   2. Lazy chunk for that route is fetched — fails (hash changed after deploy)
 *   3. Router emits NavigationError and REVERTS the browser URL to the previous page
 *   4. This handler fires window.location.reload()
 *   5. reload() restores the PREVIOUS page because Angular already reverted the URL in step 3
 *   → user sees "page reloads to the same component I was on"
 *
 * FIX:
 *   Track the in-progress navigation URL (NavigationStart) in sessionStorage.
 *   After reload the app re-navigates there automatically, landing on the
 *   intended page with fresh chunks.
 *
 *   The hasReloaded latch prevents infinite reload loops if the new build
 *   somehow still surfaces the same error class on first paint.
 */

const CHUNK_RETRY_KEY = "__chunk_reload_target";

const STALE_CHUNK_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /ChunkLoadError/i,
  /Importing a module script failed/i,
];

@Injectable()
export class ChunkErrorHandler extends ErrorHandler {
  private readonly router = inject(Router);
  private hasReloaded = false;
  private pendingNavUrl: string | null = null;

  constructor() {
    super();

    // Track the in-flight navigation target so we can restore it after reload.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.pendingNavUrl = event.url;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationError
      ) {
        this.pendingNavUrl = null;
      }
    });

    // On startup after a chunk-error reload: if a target URL was saved, navigate there.
    const retryUrl = sessionStorage.getItem(CHUNK_RETRY_KEY);
    if (retryUrl) {
      sessionStorage.removeItem(CHUNK_RETRY_KEY);
      // Wait for the first NavigationEnd (the default redirect) then go to the intended route.
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          take(1),
        )
        .subscribe(() => {
          this.router.navigateByUrl(retryUrl, { replaceUrl: true });
        });
    }
  }

  override handleError(error: unknown): void {
    const msg =
      (error as { message?: string } | null)?.message ?? String(error ?? "");
    const isStaleChunk = STALE_CHUNK_PATTERNS.some((p) => p.test(msg));
    if (isStaleChunk && !this.hasReloaded) {
      this.hasReloaded = true;
      // Save the intended destination before Angular reverts the browser URL.
      const target = this.pendingNavUrl ?? window.location.pathname;
      sessionStorage.setItem(CHUNK_RETRY_KEY, target);
      window.location.reload();
      return;
    }
    super.handleError(error);
  }
}
